import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import "intasend-inlinejs-sdk";
import Securebadge from "../Assets/intasend-assets/Logo Light.png";
import Mpesa from "../Assets/intasend-assets/M-PESA_LOGO-01.svg.png";
import VisaBadge from "../Assets/intasend-assets/hd-visa-payment-logo-png-7017516947777256ndfrewd52.png";
import MasterCardBadge from "../Assets/intasend-assets/Mastercard_2019_logo.svg.png";
import Gpay from "../Assets/intasend-assets/Google_Pay_Logo.svg.png";
import SectigoLogo from "../Assets/intasend-assets/sectigo-logo-cover.png";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px;
`;
const FormWrapper = styled.div`
  background: #fff;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 400px;
  text-align: center;
  margin-bottom: 20px;
`;
const Title = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #222;
  margin-bottom: 1.2rem;
  letter-spacing: 0.5px;
  text-transform: capitalize;
`;
const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin: 8px 0;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.3s ease;
  &:focus {
    border-color: #007bff;
    outline: none;
    box-shadow: 0 0 5px rgba(0, 123, 255, 0.3);
  }
`;
const Select = styled.select`
  width: 100%;
  padding: 12px;
  margin: 8px 0;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  background: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  &:focus {
    border-color: #007bff;
    outline: none;
  }
`;
const Button = styled.button`
  width: 100%;
  padding: 12px;
  margin-top: 12px;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: bold;
  background: ${(props) => (props.disabled ? "#ccc" : "#007bff")};
  color: ${(props) => (props.disabled ? "#666" : "#fff")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background 0.3s ease;
  &:hover {
    background: ${(props) => (props.disabled ? "#ccc" : "#0056b3")};
  }
`;
const Message = styled.p`
  margin-top: 10px;
  font-size: 1rem;
  color: ${(props) => (props.success ? "green" : "red")};
`;
const TrustBadgesContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
`;
const TrustBadge = styled.img`
  height: 50px;
  object-fit: contain;
  display: block;
`;
const SecureBadge = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0px;
  margin-top: 5px;
  text-align: center;
  img {
    width: 50%;
  }
`;
const Secure = styled.img`
  object-fit: cover;
  margin-top: -20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 50%;
`;
const SecureText = styled.p`
  margin-top: 10px;
  font-size: 1rem;
  color: #333;
`;
const ContactDetails = styled.div`
  text-align: center;
  margin-top: 10px;
  font-size: 1rem;
  color: #555;
  padding: 5px;
  border-radius: 10px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  a {
    text-decoration: none;
  }
`;

function PaymentScreen() {
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [service, setService] = useState("");
  const [customService, setCustomService] = useState("");
  const [message, setMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [currency, setCurrency] = useState("KES");

  const publicKey = process.env.REACT_APP_INTASEND_PUBLIC_KEY;
  // Ref to store the latest payment details
  const paymentDetailsRef = useRef({ email: "", service: "", customService: "", amount: "", currency: "KES" });

  // Function to send payment details to the backend
  const sendPaymentDetails = async (paymentData) => {
    try {
      const response = await fetch("https://pristineacademicconsultancy.com/Admin/generate-receipt.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(paymentData),
      });
      const data = await response.json();
      if (data.success) {
        setMessage(`✅ Payment successful`);
      } else {
        setMessage(`❌ Failed to store payment: ${data.message}`);
      }
    } catch (error) {
      setMessage("⚠️ An error occurred. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (!publicKey) {
      console.error("IntaSend Public Key is missing. Check your .env file.");
      return;
    }
    const waitForIntaSend = setInterval(() => {
      if (window.IntaSend) {
        clearInterval(waitForIntaSend);
        const intasendInstance = new window.IntaSend({
          publicAPIKey: publicKey,
          live: true,
        });
        intasendInstance
          .on("COMPLETE", (response) => {
            console.log("COMPLETE:", response);
            // Use the up-to-date payment details from the ref
            sendPaymentDetails(paymentDetailsRef.current);
          })
          .on("FAILED", (response) => {
            console.log("FAILED:", response);
            setMessage("❌ Payment failed. Please try again.");
            setIsProcessing(false);
          })
          .on("IN-PROGRESS", () => {
            console.log("IN-PROGRESS ...");
            setMessage("⌛ Payment is processing...");
          });
      }
    }, 500);

    return () => clearInterval(waitForIntaSend);
  }, [publicKey]);

  const handlePaymentStart = () => {
    if (!email.trim() || !service.trim() || !amount) {
      setMessage("⚠️ Please fill in all required fields.");
      return;
    }
    setMessage("");
    setIsProcessing(true);

    // Update the ref with the current payment details before initiating payment
    paymentDetailsRef.current = {
      email,
      service,
      customService: customService || "N/A",
      amount,
      currency,
    };

    // The payment process is started here. The "COMPLETE" event will later use the updated ref values.
  };

  return (
    <Container>
      <FormWrapper>
        <Title>Trusted & Secure Payment</Title>
        {message && <Message success={message.startsWith("✅")}>{message}</Message>}
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" required />
        <Select value={service} onChange={(e) => setService(e.target.value)} required>
          <option value="">Select Service</option>
          <option value="academic_writing">Study Abroad Service</option>
          <option value="mentorship">Scientific and Academic Writing</option>
          <option value="study_abroad">Working Abroad Service</option>
          <option value="mentorship">Academic and Professional Mentorship</option>
          <option value="appointment">Appointment</option>
          <option value="webinar">Webinar</option>
          <option value="Other">Other</option>
        </Select>
        {service === "Other" && (
          <Input type="text" value={customService} onChange={(e) => setCustomService(e.target.value)} placeholder="Enter custom service" required />
        )}
        <Select value={currency} onChange={(e) => setCurrency(e.target.value)} required>
          <option value="KES">Kenyan Shilling (KES)</option>
          <option value="USD">US Dollar (USD)</option>
        </Select>
        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter amount" required />
        <Button
          disabled={isProcessing || !email || !service || !amount}
          className="intaSendPayButton"
          onClick={handlePaymentStart}
          data-amount={amount || 10}
          data-currency={currency}
        >
          {isProcessing ? "Processing..." : `Pay Now (${currency})`}
        </Button>
        <SecureBadge>
          <SecureText>Secured By</SecureText>
          <Secure src={SectigoLogo} alt="Secured by Sectigo" style={{ height: "50px", marginTop: "10px" }} />
        </SecureBadge>
        <ContactDetails>
          <p>
            For any inquiries, <a href="mailto:pristineacademicconsultancy@gmail.com">contact us</a>.
          </p>
        </ContactDetails>
      </FormWrapper>
      <TrustBadgesContainer>
        <TrustBadge src={Securebadge} alt="IntaSend Trust Badge" />
        <TrustBadge src={Mpesa} alt="M-PESA" />
        <TrustBadge src={VisaBadge} alt="Visa Badge" />
        <TrustBadge src={Gpay} alt="Google Pay" />
        <TrustBadge src={MasterCardBadge} alt="MasterCard Badge" />
      </TrustBadgesContainer>
    </Container>
  );
}

export default PaymentScreen;
